/**
 * CatchballTossButton — the one "Toss the ball" trigger + level chooser used
 * everywhere a Catchball toss happens (owner ruling 2026-09-22, #14: one
 * button, then choose the level — replaces the old per-level button text,
 * "Send the ball" for a toss to the middle tier and "Send to the front line"
 * for a toss to the bottom tier; both now read CATCHBALL_LABELS.sendTheBall,
 * "Toss the ball").
 *
 * At any moment a loop's state machine allows exactly one next toss target —
 * catchball_toss_to_middle only runs from 'drafting'
 * (supabase/migrations/663_catchball_toss_middle_depth1_holder.sql) and
 * catchball_toss_to_bottom only runs from 'middle_working'
 * (supabase/migrations/590_bind_possession_actor_authority.sql) — so there is
 * never a real either/or to pick between. The chooser still lists every level
 * and names why an unavailable one is unavailable, rather than firing the
 * toss on a bare click with no confirmation of where it is headed (today's
 * "Send to the front line" behavior).
 *
 * Reuses ds/Modal + ds/Choice for the dialog chrome, same pattern as
 * CatchballUndoAction in this folder.
 */
import { useState } from 'react';
import { Send } from 'lucide-react';
import { Modal } from '../../ds/Modal';
import { Radio, ChoiceGroup } from '../../ds/Choice';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';

export type CatchballTossLevel = 'middle' | 'bottom';

export interface CatchballTossLevelOption {
  value: CatchballTossLevel;
  /** e.g. "Middle tier", "Front line". */
  label: string;
  description?: string;
  /** Why this level can't be chosen right now — shown next to it, not hidden. */
  disabledReason?: string;
}

export interface CatchballTossButtonProps {
  /** Every level worth naming at this step, in display order. */
  levels: CatchballTossLevelOption[];
  /** Runs on confirm; the caller performs the actual toss (navigate or RPC). */
  onConfirm: (level: CatchballTossLevel) => void;
  disabled?: boolean;
  busy?: boolean;
  /** Trigger button styling. Defaults to the primary Catchball action style. */
  className?: string;
}

const DEFAULT_TRIGGER_CLASS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

export function CatchballTossButton({
  levels,
  onConfirm,
  disabled = false,
  busy = false,
  className,
}: CatchballTossButtonProps) {
  const [open, setOpen] = useState(false);
  const enabledLevels = levels.filter((l) => !l.disabledReason);
  const [selected, setSelected] = useState<CatchballTossLevel | null>(
    enabledLevels[0]?.value ?? null,
  );

  const openChooser = () => {
    setSelected(enabledLevels[0]?.value ?? null);
    setOpen(true);
  };

  const confirm = () => {
    if (!selected) return;
    setOpen(false);
    onConfirm(selected);
  };

  return (
    <>
      <button
        type="button"
        onClick={openChooser}
        disabled={disabled || busy || enabledLevels.length === 0}
        className={className ?? DEFAULT_TRIGGER_CLASS}
      >
        <Send className="h-3 w-3" />
        {CATCHBALL_LABELS.sendTheBall}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={CATCHBALL_LABELS.sendTheBall}
        description="Choose the level this toss lands on."
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="border border-[var(--rule)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={!selected || busy}
              className="bg-[var(--slate)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-50"
            >
              {busy ? 'Tossing…' : CATCHBALL_LABELS.sendTheBall}
            </button>
          </>
        }
      >
        <ChoiceGroup legend="Level">
          {levels.map((level) => (
            <Radio
              key={level.value}
              name="catchball-toss-level"
              label={level.label}
              description={level.disabledReason ?? level.description}
              checked={selected === level.value}
              disabled={!!level.disabledReason}
              onChange={() => setSelected(level.value)}
            />
          ))}
        </ChoiceGroup>
      </Modal>
    </>
  );
}
